import { useEffect, useState } from 'react';
import { Bot, PenLine, AlertTriangle, Route } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { aiAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AIChatPanel } from './AIChatPanel';
import { AIWriterPanel } from './AIWriterPanel';
import { LeadRoutingPanel } from './LeadRoutingPanel';

export default function AIStudio() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const canManageRouting = !!(user?.is_super_admin || user?.permissions?.leads_assign);

  useEffect(() => {
    aiAPI.status().then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false }));
  }, []);

  const disabled = status ? !status.configured : true;

  return (
    <div data-testid="ai-studio" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <Bot className="w-5 h-5 text-teal-600" /> AI Studio
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Claude helps you answer, write and summarise — faster follow-ups, less typing.</p>
        </div>
        {status?.model && <span data-testid="ai-studio-model" className="text-xs text-gray-500 dark:text-gray-400">Model: {status.model}</span>}
      </div>
      {status && !status.configured && (
        <div data-testid="ai-studio-not-configured" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Claude isn&apos;t connected yet. Ask your administrator to add the Anthropic API key.
        </div>
      )}
      <Tabs defaultValue="assistant" className="space-y-4">
        <TabsList className="bg-gray-100 dark:bg-slate-700">
          <TabsTrigger value="assistant" data-testid="ai-studio-tab-assistant" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Bot className="w-4 h-4 mr-1" /> Assistant
          </TabsTrigger>
          <TabsTrigger value="writer" data-testid="ai-studio-tab-writer" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <PenLine className="w-4 h-4 mr-1" /> Writer
          </TabsTrigger>
          {canManageRouting && (
            <TabsTrigger value="routing" data-testid="ai-studio-tab-routing" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
              <Route className="w-4 h-4 mr-1" /> Lead Routing
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="assistant"><AIChatPanel disabled={disabled} /></TabsContent>
        <TabsContent value="writer"><AIWriterPanel disabled={disabled} /></TabsContent>
        {canManageRouting && <TabsContent value="routing"><LeadRoutingPanel /></TabsContent>}
      </Tabs>
    </div>
  );
}
